import { Stack } from '@mui/material';
import { createLinkedinSearchProject, getLinkedinUrlSearchResults, startLinkedinSearch } from 'API/linkedinsearchproject';
import { createProjectWrapper } from 'API/project-wrapper';
import { isSameDay, parseISO } from 'date-fns';
import { CookieErrors, LinkedinTypes } from 'enums';
import { FileUploadMatchColumns } from 'features/co-pilot/co-pilot.types';
import LinkedinCrawlerOverview from 'features/linkedin-crawler/linkedin-crawler-overview/linkedin-crawler-overview';
import FileColumnDialog from 'features/linkedin-crawler/linkedin-profile-finder/file-column-dialog';
import {
  FileColumnVariants,
  LinkedinSearchProject,
  SearchTypes,
} from 'features/linkedin-crawler/linkedin-profile-finder/linkedin-profile-finder.types';
import PaiStepCollapsible from 'features/ui/base-components/pai-step-collapsible';
import saveAs from 'file-saver';
import useLinkedinCookies from 'hooks/use-linkedin-cookies';
import _ from 'lodash';
import { parse } from 'papaparse';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import useErrorStore from 'stores/error-store';
import useLimitReachedStore from 'stores/limit-reached-store';
import { makeStyles } from 'tss-react/mui';
import { CookieError } from 'types/cookies.types';
import * as XLSX from 'xlsx';
import FindLinkedinUrls from '../features/linkedin-crawler/linkedin-profile-finder/find-linkedin-urls';
import ProvideContactInformation from '../features/linkedin-crawler/linkedin-profile-finder/provide-contact-information';
import CookieDialog from '../features/linkedin-crawler/profile-generator/cookie-error-dialog';
import { useQueryProjectWrapper } from '../features/linkedin-crawler/profile-generator/use-query-project-wrapper';
import EnterProjectNameDialog from '../features/text-generation/enter-project-name-dialog';

enum Steps {
  CSV_UPLOAD = 'csvUpload',
  FIND_LINKEDIN_URLS = 'findLinkedinUrls',
}

const LinkedinProfileFinder: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const handleOpenLimitReachedDialog = useLimitReachedStore((state) => state.handleOpenLimitReachedDialog);
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const appBarNavigation = useAppBarNavigationStore((state) => state.activeTab);

  const { sessionCookie, liAtCookie, seatCookie } = useLinkedinCookies();

  const { urlFinderProjectName, urlFinderProjects, setUrlFinderWrapperQuery, resetQuery, fetchUrlFinderProjectsFromWrapper } =
    useQueryProjectWrapper();

  const [activeStep, setActiveStep] = useState<Steps>(Steps.CSV_UPLOAD);

  const [linkedinProduct, setLinkedinProduct] = useState<LinkedinTypes>(LinkedinTypes.LINKEDIN);
  const [searchType, setSearchType] = useState<SearchTypes>(SearchTypes.CONTACTS);

  const [fileColumnDialog, setFileColumnDialog] = useState<{
    open: boolean;
    columns: { text: string; type: string }[];
    data: string[][];
    hasTableHeading: boolean;
  }>({
    open: false,
    columns: [],
    data: [],
    hasTableHeading: true,
  });
  const [keywords, setKeywords] = useState<string[]>([]);
  const [enterProjectNameDialog, setEnterProjectNameDialog] = useState<{ open: boolean; projectName: string; nameTakenError?: boolean }>({
    open: false,
    projectName: '',
  });

  const [cookieDialog, setCookieDialog] = useState<{
    open: boolean;
    liAtCookie: string;
    sessionCookie: string;
    error: CookieError | undefined;
  }>({
    open: false,
    liAtCookie: '',
    sessionCookie: '',
    error: undefined,
  });
  const [startSearchLoading, setStartSearchLoading] = useState(false);

  const handleSetActiveStep = (step: Steps) => {
    return () => {
      setActiveStep(step);
    };
  };

  const resetAll = () => {
    setActiveStep(Steps.CSV_UPLOAD);
    setLinkedinProduct(LinkedinTypes.LINKEDIN);
    setSearchType(SearchTypes.CONTACTS);
    setKeywords([]);
    resetQuery();
  };

  useEffect(() => {
    if (!_.isEmpty(urlFinderProjects)) {
      setActiveStep(Steps.FIND_LINKEDIN_URLS);
    } else {
      setActiveStep(Steps.CSV_UPLOAD);
    }
  }, [urlFinderProjects]);

  /**
   * Contact info handlers
   */

  const handleLinkedinProductChange = (value: string) => {
    return () => {
      setLinkedinProduct(value as LinkedinTypes);
    };
  };

  const handleSearchTypeChange = (value: string) => {
    return () => {
      setSearchType(value as SearchTypes);
    };
  };

  /**
   * CSV handlers
   */

  const onCsvDrop = useCallback((acceptedFiles: any) => {
    acceptedFiles.forEach((file: any) => {
      const reader = new FileReader();

      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');

      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        reader.onload = (e: any) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const parsedData: string[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (!_.isNil(parsedData)) {
            handleOpenFileColumnDialog(parsedData);
          }
        };
        reader.readAsArrayBuffer(file);
      } else if (file.name.endsWith('.csv')) {
        reader.onload = (e: any) => {
          const text = e.target.result;
          const parsedData = parse(text, { delimiter: ';' });

          if (!_.isNil(parsedData.data)) {
            handleOpenFileColumnDialog(parsedData.data as string[][]);
          }
        };
        reader.readAsText(file);
      } else {
        console.log('Unsupported file type');
      }
    });
  }, []);

  const handleOpenFileColumnDialog = (data: string[][]) => {
    const columns = data[1];
    const newColumns = Array.from({ length: columns.length }, (_, index) => ({
      text: columns[index] || '',
      type: '',
    }));

    setFileColumnDialog((prev) => ({
      ...prev,
      open: true,
      data,
      columns: newColumns,
    }));
  };

  const handleColumnTypeChange = (idx: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const updateFileColumnDialog = { ...fileColumnDialog };

      if (event.target.value === FileUploadMatchColumns.EMPTY) {
        updateFileColumnDialog.columns[idx].type = '';
      } else {
        const existingSameType = updateFileColumnDialog.columns.find((col) => col.type === event.target.value);

        if (!_.isNil(existingSameType)) {
          existingSameType.type = '';
        }

        updateFileColumnDialog.columns[idx].type = event.target.value;
      }

      setFileColumnDialog(updateFileColumnDialog);
    };
  };

  const handleHasTableHeadingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { data, columns } = fileColumnDialog;

    const columnStrings = data[event.target.checked ? 1 : 0];

    if (!columnStrings) return;

    let newColumns: { text: string; type: string }[] = [];

    columnStrings.forEach((col, index) => {
      const updateColumns = [...columns];
      updateColumns[index].text = col;
      newColumns = updateColumns;
    });

    setFileColumnDialog((prev) => ({ ...prev, columns: newColumns, hasTableHeading: event.target.checked }));
  };

  const handleCloseFileColumnDialog = () => {
    setFileColumnDialog({ open: false, data: [], columns: [], hasTableHeading: true });
  };

  const handleAcceptColumns = () => {
    const { data, columns, hasTableHeading } = fileColumnDialog;

    const transformedData: string[] = data
      .filter((row) => row.length > 1)
      .map((row: string[]) => {
        const firstNameIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.FIRST_NAME);
        const lastNameIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.LAST_NAME);
        const companyIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.COMPANY);
        const emailIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.EMAIL);
        // website visitors
        const roleIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.ROLE);
        const countryIndex = columns.findIndex((col) => col.type === FileUploadMatchColumns.COUNTRY);

        let contactFirstName: string | undefined = undefined;
        let contactLastName: string | undefined = undefined;
        let contactCompany: string | undefined = undefined;
        let contactEmail: string | undefined = undefined;
        let contactRole: string | undefined = undefined;
        let contactCountry: string | undefined = undefined;

        if (!_.isNil(firstNameIndex) && firstNameIndex >= 0) {
          contactFirstName = row[firstNameIndex];
        }

        if (!_.isNil(lastNameIndex) && lastNameIndex >= 0) {
          contactLastName = row[lastNameIndex];
        }

        if (!_.isNil(companyIndex) && companyIndex >= 0) {
          contactCompany = row[companyIndex];
        }

        if (!_.isNil(emailIndex) && emailIndex >= 0) {
          contactEmail = row[emailIndex];
        }

        if (!_.isNil(roleIndex) && roleIndex >= 0) {
          contactRole = row[roleIndex];
        }

        if (!_.isNil(countryIndex) && countryIndex >= 0) {
          contactCountry = row[countryIndex];
        }

        const searchKeywords = [];

        if (searchType === SearchTypes.CONTACTS) {
          if (contactFirstName) {
            searchKeywords.push(contactFirstName);
          }

          if (contactLastName) {
            searchKeywords.push(contactLastName);
          }

          if (contactCompany) {
            searchKeywords.push(contactCompany);
          }

          if (contactEmail) {
            searchKeywords.push(contactEmail);
          }
        }

        if (searchType === SearchTypes.WEBSITE) {
          if (contactCompany) {
            searchKeywords.push(contactCompany);
          }

          if (contactRole) {
            searchKeywords.push(contactRole);
          }

          if (contactCountry) {
            searchKeywords.push(contactCountry);
          }
        }

        return searchKeywords.join(';');
      });

    const contacts = hasTableHeading ? _.tail(transformedData) : transformedData;
    const filterContacts = contacts.filter((contact) => contact !== '');

    setKeywords(filterContacts);
    handleCloseFileColumnDialog();
  };

  const removeKeywords = () => {
    setKeywords([]);
  };

  /**
   * Enter project name dialog handlers
   */

  const handleOpenProjectNameDialog = () => {
    setEnterProjectNameDialog({ ...enterProjectNameDialog, open: true });
  };

  const handleCloseProjectNameDialog = () => {
    setEnterProjectNameDialog({ open: false, projectName: '', nameTakenError: undefined });
  };

  const handleProjectNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnterProjectNameDialog({ ...enterProjectNameDialog, projectName: event.target.value });
  };

  const handleAcceptProjectName = async () => {
    const tempKeywords = _.cloneDeep(keywords);
    const chunkArray = _.chunk(tempKeywords, 200);

    const allRequests = chunkArray.map((keywords, index) =>
      createLinkedinSearchProject(
        chunkArray.length > 1 ? `${enterProjectNameDialog.projectName}#${index + 1}` : enterProjectNameDialog.projectName,
        keywords,
        linkedinProduct,
        searchType
      )
    );

    const resProjects = await Promise.all(allRequests);

    if (!resProjects.some((request) => request.error)) {
      const projectIds = resProjects.map((request) => request.id);
      const resWrapper = await createProjectWrapper(enterProjectNameDialog.projectName, 'li_search', projectIds);

      if (resWrapper && !resWrapper.error) {
        const newWrapper = resWrapper.projectWrapperId;

        setUrlFinderWrapperQuery(newWrapper);
        setActiveStep(Steps.FIND_LINKEDIN_URLS);
        setLinkedinProduct(LinkedinTypes.LINKEDIN);
        setSearchType(SearchTypes.CONTACTS);
        setKeywords([]);

        handleCloseProjectNameDialog();
      } else {
        handleOpenErrorDialog(resWrapper?.error, '/projects/wrappers', 'post');
      }
    } else {
      if (
        resProjects.some(
          (request) =>
            request.error?.code === 401 &&
            (request.error?.message === 'This account is not authorized to access the requested product.' ||
              request.error?.message === 'not Enough credits')
        )
      ) {
        handleOpenLimitReachedDialog();
      } else {
        resProjects.forEach((response) => {
          if (!_.isNil(response.error)) {
            handleOpenErrorDialog(response?.error, '/linkedinsearchproject', 'post');
          }
        });
      }
    }
  };

  /**
   * Cookie dialog handlers
   */

  const handleOpenCookieDialog = (msg: CookieErrors) => {
    return () => {
      setCookieDialog({ ...cookieDialog, open: true, error: { message: msg, cookie: liAtCookie } });
    };
  };

  const handleCloseCookieDialog = () => {
    setCookieDialog({ open: false, liAtCookie: '', sessionCookie: '', error: undefined });
  };

  /**
   * URL search handlers
   */

  const isSearchTodayPossible = (liAt: string | undefined, newAnalysesAmount: number) => {
    if (!_.isNil(liAt) && liAt !== '') {
      const storedAnalyses = localStorage.getItem('linkedin-search');
      const analysisArray: { startedAt: string; dailyLimit: number; liAt: string }[] = storedAnalyses ? JSON.parse(storedAnalyses) : [];
      const findAnalysis = analysisArray.find((analysis) => analysis.liAt === liAt);

      const storedLimit = findAnalysis?.dailyLimit;
      const storedTime = findAnalysis?.startedAt;

      if (!_.isNil(storedLimit) && !_.isNil(storedTime)) {
        const now = new Date();
        const storedTimeParsed: Date = parseISO(storedTime);
        const sameDay = isSameDay(now, storedTimeParsed);

        const restAvailable = storedLimit - newAnalysesAmount;

        if (!sameDay) {
          return true;
        } else if (sameDay && restAvailable >= 0) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    }
    return false;
  };

  const startSearch = (project: LinkedinSearchProject) => {
    return async () => {
      if (isSearchTodayPossible(liAtCookie, project.searchKeywords.length)) {
        if (liAtCookie !== '' && sessionCookie !== '') {
          setStartSearchLoading(true);

          const res = await startLinkedinSearch(project.id, liAtCookie, sessionCookie, seatCookie);
          if (!res.error) {
            setTimeout(() => {
              fetchUrlFinderProjectsFromWrapper();
              setStartSearchLoading(false);
            }, 5000);

            const now = new Date();
            const storedAnalyses = localStorage.getItem('linkedin-search');
            const analysisArray: { startedAt: string; dailyLimit: number; liAt: string }[] = storedAnalyses ? JSON.parse(storedAnalyses) : [];
            const findAnalysis = analysisArray.find((analysis) => analysis.liAt === liAtCookie);
            const maxLimit = 1000;
            if (!_.isNil(findAnalysis)) {
              const storedTimeParsed: Date = parseISO(findAnalysis.startedAt);
              const sameDay = isSameDay(now, storedTimeParsed);
              if (sameDay) {
                findAnalysis.dailyLimit -= project.searchKeywords.length;
              } else {
                findAnalysis.startedAt = now.toISOString();
                findAnalysis.dailyLimit = maxLimit;
              }
            } else {
              analysisArray.push({
                startedAt: now.toISOString(),
                dailyLimit: maxLimit - project.searchKeywords.length,
                liAt: liAtCookie || '',
              });
            }
            localStorage.setItem('linkedin-search', JSON.stringify(analysisArray));
          } else {
            if (res.error.code === 400 && res.error.message === 'Invalid Cookies provided') {
              handleOpenCookieDialog(CookieErrors.INVALID_COOKIES)();
            } else {
              handleOpenErrorDialog(res?.error, '/linkedinsearchproject/:projectId/startSearch', 'post');
            }

            setStartSearchLoading(false);
          }
        } else {
          handleOpenCookieDialog(CookieErrors.INVALID_COOKIES)();
        }
      } else {
        handleOpenCookieDialog(CookieErrors.HOUR_LIMIT)();
      }
    };
  };

  const restartSearch = (project: LinkedinSearchProject) => {
    return async () => {
      if (liAtCookie !== '' && sessionCookie !== '') {
        setStartSearchLoading(true);

        const res = await startLinkedinSearch(project.id, liAtCookie, sessionCookie, seatCookie);
        if (!res.error) {
          setTimeout(() => {
            fetchUrlFinderProjectsFromWrapper();
            setStartSearchLoading(false);
          }, 5000);
        } else {
          if (res.error.code === 400 && res.error.message === 'Invalid Cookies provided') {
            handleOpenCookieDialog(CookieErrors.INVALID_COOKIES)();
          } else {
            handleOpenErrorDialog(res?.error, '/linkedinsearchproject/:projectId/resumeSearch', 'post');
          }

          setStartSearchLoading(false);
        }
      } else {
        handleOpenCookieDialog(CookieErrors.INVALID_COOKIES)();
      }
    };
  };

  const downloadSearchResults = (id: string) => {
    return async () => {
      const res = await getLinkedinUrlSearchResults(id);

      if (res && !res.error) {
        const newBlob = new Blob(['\ufeff', res], { type: 'text/csv' });
        saveAs(newBlob, `${t('linkedinContactSearch')}.csv`);
      } else {
        handleOpenErrorDialog(res?.error, '/linkedinsearchproject/:projectId/getResults', 'get');
      }
    };
  };

  return (
    <>
      {appBarNavigation === NavigationStates.TOOL ? (
        <div className={classes.container}>
          <Stack className={classes.steps} direction={{ xs: 'column', xl: 'row' }} gap={1}>
            <PaiStepCollapsible
              stepNumber={1}
              stepName={t('provideContactInformation')}
              expanded={activeStep === Steps.CSV_UPLOAD}
              variant={_.isEmpty(urlFinderProjects) ? 'edit' : 'success'}
              disabled={!_.isEmpty(urlFinderProjects)}
              breakpoint={'xl'}
              toggle={handleSetActiveStep(Steps.CSV_UPLOAD)}
            >
              <ProvideContactInformation
                linkedinProduct={linkedinProduct}
                searchType={searchType}
                keywords={keywords}
                onCsvDrop={onCsvDrop}
                removeKeywords={removeKeywords}
                handleOpenProjectNameDialog={handleOpenProjectNameDialog}
                handleLinkedinProductChange={handleLinkedinProductChange}
                handleSearchTypeChange={handleSearchTypeChange}
              />
            </PaiStepCollapsible>
            <PaiStepCollapsible
              stepNumber={1}
              stepName={t('findLinkedinUrls')}
              expanded={activeStep === Steps.FIND_LINKEDIN_URLS}
              variant={'edit'}
              disabled={_.isEmpty(urlFinderProjects)}
              breakpoint={'xl'}
              toggle={handleSetActiveStep(Steps.FIND_LINKEDIN_URLS)}
            >
              <FindLinkedinUrls
                urlFinderProjectName={urlFinderProjectName}
                urlFinderProjects={urlFinderProjects}
                startSearchLoading={startSearchLoading}
                startSearch={startSearch}
                restartSearch={restartSearch}
                downloadSearchResults={downloadSearchResults}
              />
            </PaiStepCollapsible>
          </Stack>
        </div>
      ) : appBarNavigation === NavigationStates.PROJECTS ? (
        <LinkedinCrawlerOverview title={t('urlFinder')} type={'li_search'} resetAll={resetAll} />
      ) : null}

      <FileColumnDialog
        open={fileColumnDialog.open}
        variant={
          searchType === SearchTypes.CONTACTS
            ? FileColumnVariants.LINKEDIN_PROFILE_FINDER_CONTACTS_UPLOAD
            : FileColumnVariants.LINKEDIN_PROFILE_FINDER_WEBSITE_VISITORS_UPLOAD
        }
        columns={fileColumnDialog.columns}
        hasTableHeading={fileColumnDialog.hasTableHeading}
        handleColumnTypeChange={handleColumnTypeChange}
        handleHasTableHeadingChange={handleHasTableHeadingChange}
        handleClose={handleCloseFileColumnDialog}
        handleAcceptColumns={handleAcceptColumns}
      />
      <EnterProjectNameDialog
        open={enterProjectNameDialog.open}
        projectName={enterProjectNameDialog.projectName}
        nameTakenError={enterProjectNameDialog.nameTakenError}
        handleProjectNameChange={handleProjectNameChange}
        handleClose={handleCloseProjectNameDialog}
        acceptProjectName={handleAcceptProjectName}
      />
      <CookieDialog open={cookieDialog.open} error={cookieDialog.error} handleClose={handleCloseCookieDialog} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: '8px',
    background: theme.palette.backgrounds.main,
    height: 'calc(100vh - 60px - 32px)',
    maxHeight: 'calc(960px - 60px - 32px)',
    width: '100%',
    [theme.breakpoints.down('xl')]: {
      height: 'fit-content',
      maxHeight: 'unset',
    },
  },
  steps: {
    height: '100%',
  },
  projects: {
    maxWidth: 'calc(1920px - 266px)',
    padding: '20px 16px 40px 48px',
    [theme.breakpoints.down('lg')]: {
      padding: '24px 8px 40px 8px',
      margin: 0,
    },
    [theme.breakpoints.down('sm')]: {
      padding: '16px 8px 32px 8px',
    },
  },
}));

export default LinkedinProfileFinder;
